import React, { useEffect, useRef, useState } from "react";

/**
 * ChatPanel – Bond-styled sessiechat
 *
 * Props:
 *  - messages: Array<{ id, name, text, ts, userId? }>
 *  - onSend(text: string)
 *  - onClose?(): void   (optioneel, voor een sluitknop)
 *  - currentUserId?: string | number  (optioneel, om je eigen berichten te highlighten)
 */
export default function ChatPanel({
  messages,
  onSend,
  onClose,
  currentUserId,
}) {
  const [draft, setDraft] = useState("");
  const listRef = useRef(null);

  // auto-scroll naar onder bij nieuwe berichten
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  function handleSubmit(e) {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    if (onSend) onSend(text);
    setDraft("");
  }

  function formatTime(ts) {
    if (!ts) return "";
    try {
      const d = new Date(ts);
      return d.toLocaleTimeString("nl-NL", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  }

  return (
    <div
      style={{
        width: 280,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background:
          "radial-gradient(circle at top left, #020617 0%, #020617 40%, #020617 100%)",
        borderRight: "1px solid rgba(15,23,42,0.9)",
        boxShadow: "0 24px 80px rgba(15,23,42,0.9)",
        color: "#e5e7eb",
        zIndex: 25,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "10px 12px 8px 14px",
          borderBottom: "1px solid rgba(15,23,42,0.9)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
            }}
          >
            Chat
          </div>
          <div
            style={{
              fontSize: 11,
              color: "#9ca3af",
            }}
          >
            Sessiechat met iedereen in deze Bond.
          </div>
        </div>

        {onClose && (
          <button
            type="button"
            onClick={onClose}
            style={{
              borderRadius: 999,
              border: "1px solid rgba(148,163,184,0.4)",
              width: 26,
              height: 26,
              display: "grid",
              placeItems: "center",
              background:
                "radial-gradient(circle at top, rgba(15,23,42,0.95), rgba(15,23,42,0.9))",
              color: "#9ca3af",
              cursor: "pointer",
              fontSize: 14,
            }}
            aria-label="Chat sluiten"
          >
            ×
          </button>
        )}
      </div>

      {/* Berichtenlijst */}
      <div
        ref={listRef}
        className="bond-scroll"
        style={{
          flex: 1,
          padding: "10px 10px 12px 12px",
          overflowY: "auto",
          fontSize: 13,
        }}
      >
        {messages && messages.length > 0 ? (
          messages.map((m) => {
            const isSelf =
              currentUserId != null &&
              m.userId != null &&
              String(m.userId) === String(currentUserId);

            return (
              <div
                key={m.id}
                style={{
                  marginBottom: 8,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: isSelf ? "flex-end" : "flex-start",
                }}
              >
                <div
                  style={{
                    maxWidth: "90%",
                    background: isSelf
                      ? "rgba(79,70,229,0.26)"
                      : "rgba(15,23,42,0.85)",
                    borderRadius: 14,
                    padding: "6px 9px 6px 9px",
                    border: isSelf
                      ? "1px solid rgba(129,140,248,0.9)"
                      : "1px solid rgba(31,41,55,0.9)",
                    boxShadow: isSelf
                      ? "0 12px 30px rgba(15,23,42,0.9)"
                      : "0 10px 24px rgba(15,23,42,0.8)",
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: isSelf ? "#c7d2fe" : "#9ca3af",
                      marginBottom: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 6,
                    }}
                  >
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {m.name || (isSelf ? "Jij" : "Gast")}
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        color: "#6b7280",
                        flexShrink: 0,
                      }}
                    >
                      {formatTime(m.ts)}
                    </span>
                  </div>
                  <div
                    style={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {m.text}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div
            style={{
              fontSize: 12,
              color: "#6b7280",
              marginTop: 6,
            }}
          >
            Nog geen berichten. Begin de chat met een{" "}
            <span style={{ color: "#c4b5fd" }}>goede vraag</span> of{" "}
            <span style={{ color: "#f97316" }}>🔥 reactie</span>.
          </div>
        )}
      </div>

      {/* Inputbalk */}
      <form
        onSubmit={handleSubmit}
        style={{
          borderTop: "1px solid rgba(15,23,42,0.9)",
          padding: "8px 8px 10px 8px",
          background:
            "radial-gradient(circle at top, rgba(79,70,229,0.15), rgba(2,6,23,1))",
          display: "flex",
          gap: 6,
        }}
      >
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Stuur een bericht…"
          style={{
            flex: 1,
            borderRadius: 999,
            border: "1px solid rgba(75,85,99,0.9)",
            padding: "8px 12px",
            fontSize: 13,
            background: "#020617",
            color: "#e5e7eb",
            outline: "none",
          }}
        />
        <button
          type="submit"
          disabled={!draft.trim()}
          style={{
            borderRadius: 999,
            border: "none",
            padding: "8px 14px",
            fontSize: 13,
            fontWeight: 500,
            cursor: draft.trim() ? "pointer" : "default",
            opacity: draft.trim() ? 1 : 0.55,
            background:
              "linear-gradient(135deg, #4f46e5 0%, #6366f1 40%, #ec4899 100%)",
            color: "#fff",
            boxShadow: draft.trim()
              ? "0 10px 30px rgba(15,23,42,0.9)"
              : "none",
            whiteSpace: "nowrap",
          }}
        >
          Stuur
        </button>
      </form>
    </div>
  );
}
